/**
 * Non-Critical JavaScript Loader
 * Loads JavaScript libraries that are not needed for above-the-fold content
 * This improves initial page load performance by deferring non-essential scripts
 */

import { loadJS, loadDeferredJS } from './utils/jsLoader';

/**
 * Load all non-critical JavaScript files asynchronously
 * These files are not needed for the initial page render
 */
export function loadNonCriticalScripts() {
  // List of non-critical JavaScript files that can be loaded after initial render
  // Only including JS for libraries that are actually used in the codebase
  const nonCriticalJS = [
    // jQuery and Bootstrap JS - not needed for initial render
    // These are used for interactive components that load later
    {
      src: 'https://code.jquery.com/jquery-3.6.0.min.js',
      type: 'text/javascript'
    },
    {
      src: 'https://cdn.jsdelivr.net/npm/bootstrap@4.6.0/dist/js/bootstrap.bundle.min.js',
      type: 'text/javascript'
    }
  ];

  // Load all non-critical JavaScript files
  const loadPromises = nonCriticalJS.map(jsFile => 
    loadJS(jsFile.src, jsFile.type).catch(error => {
      console.warn(`Failed to load non-critical JS: ${jsFile.src}`, error);
    })
  );

  return Promise.all(loadPromises);
}

/**
 * Load critical JavaScript files that are needed for above-the-fold content
 * These should be loaded synchronously to prevent functionality issues
 */
export function loadCriticalScripts() {
  // Critical JavaScript files that are needed immediately
  // These are already imported in index.js and are essential for the app to work
  // This function is here for documentation and potential future use
  return Promise.resolve();
}

/**
 * Initialize JavaScript loading strategy
 * Call this after the initial React render
 */
export function initializeJSLoading() {
  // Load non-critical JS after a delay to not block initial render
  setTimeout(() => {
    loadNonCriticalScripts().then(() => {
      console.log('Non-critical JavaScript loaded successfully');
    }).catch(error => {
      console.error('Error loading non-critical JavaScript:', error);
    });
  }, 200); // Slightly longer delay than CSS to ensure CSS loads first
}

/**
 * Load JavaScript libraries on demand
 * Use this for libraries that are only needed when specific features are used
 */
export function loadOnDemand(libraryName) {
  const libraries = {
    'moment': () => import('moment'),
    'lodash': () => import('lodash'),
    'swiper': () => import('swiper'),
    'slick': () => import('react-slick')
  };

  if (libraries[libraryName]) {
    return libraries[libraryName]();
  } else {
    console.warn(`Library ${libraryName} not found in on-demand loader`);
    return Promise.reject(new Error(`Library ${libraryName} not available`));
  }
}
